import { apiGet, apiPost, apiPut, type ErrorSubject } from '../../lib/api'
import { withAuth } from '../auth/token'
import type { EquipmentFeature, EquipmentType } from '../carriers/api'

/** Mirrors the truck availability slices on the API (Stage 2, 2.1). */
export type LoadType = 'FTL' | 'LTL'

export const LOAD_TYPES: readonly LoadType[] = ['FTL', 'LTL']

/** Open and Expired are the calendar's reading of a Published posting; Withdrawn is history. */
export type AvailabilityStanding = 'Open' | 'Expired' | 'Withdrawn'

export type SecurementGear = 'Chains' | 'Straps' | 'Tarps' | 'Bars'

export const SECUREMENT_GEAR: readonly SecurementGear[] = ['Chains', 'Straps', 'Tarps', 'Bars']

/** How long a posting stays open when the last day is left blank — the API's default. */
export const DEFAULT_DAYS = 3

/**
 * Why one of the posting's units cannot be offered today: the unit, and the record that
 * holds it back — the unit itself, or its carrier.
 */
export interface Holdback {
  unit: 'Truck' | 'Trailer' | 'Driver'
  culprit: ErrorSubject
}

export interface TruckAvailability {
  id: string
  /** Handed out by the database; people and emails quote the posting by it (2.4). */
  number: number
  /** The number as it is said — `TC-A-12` — on the board, the posting and every broadcast subject. */
  reference: string
  carrierId: string
  carrierName: string
  truckId: string
  truckUnitNumber: string
  /** The trailer the truck pulls; null for a straight box truck, which pulls none (TRKC-100). */
  trailerId: string | null
  trailerNumber: string | null
  driverId: string | null
  driverName: string | null
  /** yyyy-MM-dd, a business date — never an instant. */
  availableFrom: string
  /** The last available day, inclusive. */
  availableUntil: string
  city: string
  state: string
  /** Whether the place resolved on a map; matching says "distance unknown" when not. */
  located: boolean
  preferredDestinations: string[]
  /** The trailer's — or a straight box truck's own — taken when the posting was saved. */
  equipment: EquipmentType
  /** What the unit has, read as it stands today from its trailer or a box truck itself (TRKC-100). */
  features: EquipmentFeature[]
  /** A straight box truck's box in feet, as the truck states it today (TRKC-102); null for a unit with a trailer. */
  boxLengthFeet: number | null
  loadType: LoadType
  availablePallets: number | null
  availableWeightPounds: number
  reeferMinFahrenheit: number | null
  reeferMaxFahrenheit: number | null
  securement: SecurementGear[]
  specialInstructions: string | null
  minRatePerMile: number | null
  standing: AvailabilityStanding
  withdrawnAt: string | null
  /** The server's verdict, read through the cascade at the moment of reading. */
  dispatchable: boolean
  heldBackBy: Holdback[]
  createdAt: string
  /** The version an edit is made on — only a save of this posting's form moves it. */
  updatedAt: string
}

export interface TruckAvailabilityPayload {
  truckId: string | null
  trailerId: string | null
  driverId: string | null
  availableFrom: string | null
  availableUntil: string | null
  city: string
  state: string
  preferredDestinations: string[]
  loadType: LoadType
  availablePallets: number | null
  availableWeightPounds: number | null
  reeferMinFahrenheit: number | null
  reeferMaxFahrenheit: number | null
  securement: SecurementGear[]
  specialInstructions: string | null
  minRatePerMile: number | null
  /** On an edit, the `updatedAt` the form was opened on. */
  version?: string
}

/**
 * The board's situations (TRKC-90). With none chosen the board shows what is current —
 * open and expired — as it always has; HeldBack is an open posting whose unit cannot be
 * offered today; All brings the withdrawn history in too.
 */
export type BoardStatus = 'Open' | 'HeldBack' | 'Expired' | 'Withdrawn' | 'All'

export const BOARD_STATUSES: readonly BoardStatus[] = ['Open', 'HeldBack', 'Expired', 'Withdrawn', 'All']

/** How the board is read — always within its groups: open first, then expired, then withdrawn. */
export type BoardSort = 'Available' | 'Carrier' | 'State'

export const BOARD_SORTS: readonly BoardSort[] = ['Available', 'Carrier', 'State']

/** What the board is narrowed to. Every field is optional; an unknown value finds nothing. */
export interface AvailabilityFilter {
  status?: BoardStatus
  carrierId?: string
  equipment?: EquipmentType
  /** The state the unit is in, as a whole two-letter code. */
  state?: string
  /** With `availableTo`, the postings whose days touch the range; alone, those still free on or after it. */
  availableFrom?: string
  availableTo?: string
  sort?: BoardSort
}

export function listAvailability(filter: AvailabilityFilter = {}): Promise<TruckAvailability[]> {
  const query = new URLSearchParams()

  if (filter.status === 'All') {
    query.set('includeWithdrawn', 'true')
  } else if (filter.status !== undefined) {
    query.set('standing', filter.status)
  }

  for (const [key, value] of [
    ['carrierId', filter.carrierId],
    ['equipment', filter.equipment],
    ['state', filter.state],
    ['availableFrom', filter.availableFrom],
    ['availableTo', filter.availableTo],
  ] as const) {
    if (value !== undefined && value !== '') {
      query.set(key, value)
    }
  }

  // Soonest available is the board's own order: said by saying nothing.
  if (filter.sort !== undefined && filter.sort !== 'Available') {
    query.set('sort', filter.sort)
  }

  const search = query.toString()
  return apiGet<TruckAvailability[]>(`/api/availability${search === '' ? '' : `?${search}`}`, { headers: withAuth() })
}

export function getAvailability(id: string): Promise<TruckAvailability> {
  return apiGet<TruckAvailability>(`/api/availability/${id}`, { headers: withAuth() })
}

export function postAvailability(payload: TruckAvailabilityPayload): Promise<TruckAvailability> {
  return apiPost<TruckAvailability>('/api/availability', payload, { headers: withAuth() })
}

export function updateAvailability(
  id: string,
  payload: TruckAvailabilityPayload,
): Promise<TruckAvailability> {
  return apiPut<TruckAvailability>(`/api/availability/${id}`, payload, { headers: withAuth() })
}

export function withdrawAvailability(id: string): Promise<TruckAvailability> {
  return apiPut<TruckAvailability>(`/api/availability/${id}/withdrawal`, undefined, {
    headers: withAuth(),
  })
}
